/** Integração por API: chaves para o ERP ou sistema da empresa enviar produtos, estoque e preço. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Plus } from 'lucide-react';
import type { ApiKeyCreated, ApiKeyRow } from '@berytus/contracts';
import { api, dateTime } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Modal } from './CatalogSettings.tsx';
import { can } from '@berytus/contracts';

export function ApiKeysPage() {
  const me = useMe();
  const canEdit = can(me.tenant.access, 'api', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['api-keys'], queryFn: () => api<ApiKeyRow[]>('/account/api-keys'), enabled: can(me.tenant.access, 'api') });
  const [creating, setCreating] = useState(false);
  const revoke = useMutation({
    mutationFn: (k: ApiKeyRow) => api(`/account/api-keys/${k.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
  const base = `${window.location.origin}/api/public/v1`;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Integração por API</h1>
          <p>Seu ERP ou sistema envia produtos, cadastro fiscal, fotos, estoque e preço direto para a Berytus. Crie uma chave para cada sistema que vai se conectar.</p>
        </div>
        {canEdit && <button className="btn primary" type="button" onClick={() => setCreating(true)}><Plus size={16} /> Nova chave</button>}
      </div>
      {!can(me.tenant.access, 'api') ? (
        <div className="panel"><p className="muted" style={{ margin: 0 }}>Só o dono ou administradores gerenciam as chaves da API.</p></div>
      ) : (
        <div className="panel flush">
          {q.isPending ? <Loading /> : q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : q.data.length === 0 ? (
            <div className="empty"><b>Nenhuma chave</b>Crie a primeira para o seu ERP começar a enviar produtos.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="simple">
                <thead><tr><th>Nome</th><th>Chave</th><th>Acesso</th><th>Criada</th><th>Último uso</th><th>Situação</th><th /></tr></thead>
                <tbody>
                  {q.data.map((k) => (
                    <tr key={k.id} style={k.revokedAt ? { opacity: 0.55 } : undefined}>
                      <td><b>{k.name}</b>{k.createdBy && <div className="small muted">por {k.createdBy}</div>}</td>
                      <td className="code">{k.prefix}…</td>
                      <td>{k.scopes.includes('catalog:write') ? 'Leitura e envio' : k.scopes.includes('stock:write') ? 'Só estoque' : 'Só leitura'}</td>
                      <td className="small">{dateTime(k.createdAt)}</td>
                      <td className="small">{k.lastUsedAt ? dateTime(k.lastUsedAt) : '—'}</td>
                      <td>{k.revokedAt ? <Tag tone="off">revogada</Tag> : <Tag tone="ok">ativa</Tag>}</td>
                      <td style={{ textAlign: 'right' }}>
                        {canEdit && !k.revokedAt && (
                          <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={revoke.isPending}
                            onClick={() => confirm(`Revogar "${k.name}"? O sistema que usa esta chave para de conseguir enviar dados na hora.`) && revoke.mutate(k)}>Revogar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="panel" style={{ marginTop: 16, display: 'grid', gap: 10 }}>
        <h2 style={{ margin: 0 }}>Como usar</h2>
        <p className="small muted" style={{ margin: 0 }}>Envie a chave no cabeçalho <span className="code">Authorization: Bearer brt_live_…</span>. Campos que não forem enviados não mudam; um produto com erro não impede os outros da mesma chamada. Cada envio aparece em Importações.</p>
        <pre className="code" style={{ margin: 0, padding: 12, background: 'var(--surface-2)', borderRadius: 8, overflowX: 'auto', fontSize: 12.5 }}>{`# conferir a chave
curl -H "Authorization: Bearer $BERYTUS_API_KEY" ${base}/ping

# enviar produtos (até 500 por chamada)
curl -X PUT ${base}/products \\
  -H "Authorization: Bearer $BERYTUS_API_KEY" -H "Content-Type: application/json" \\
  -d '{"products":[{"sku":"123","title":"Vitamina C 1g 30 comp.","gtins":["7891234567895"],"cost":12.5,"stock":40}]}'

# só estoque (até 5.000 por chamada)
curl -X PUT ${base}/stock -H "Authorization: Bearer $BERYTUS_API_KEY" -H "Content-Type: application/json" \\
  -d '{"items":[{"sku":"123","quantity":38}]}'`}</pre>
      </div>
      {creating && <CreateKey onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateKey({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [access, setAccess] = useState<'full' | 'read' | 'stock'>('full');
  const [copied, setCopied] = useState(false);
  const create = useMutation({
    mutationFn: () => api<ApiKeyCreated>('/account/api-keys', { method: 'POST', json: { name: name.trim(), access } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
  if (create.data) {
    const key = create.data.key;
    return (
      <Modal title="Chave criada" onClose={onClose}>
        <p style={{ margin: 0 }}><b>Copie agora:</b> por segurança, a chave completa não aparece de novo. Se perder, revogue e crie outra.</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="input code" readOnly value={key} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1 }} />
          <button className="btn primary" type="button" onClick={async () => { await navigator.clipboard.writeText(key).catch(() => {}); setCopied(true); }}>
            <Copy size={16} /> {copied ? 'Copiada' : 'Copiar'}
          </button>
        </div>
        <p className="small muted" style={{ margin: 0 }}>Guarde a chave no servidor do seu sistema (variável de ambiente ou arquivo de configuração), nunca no código ou em planilhas compartilhadas.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn" type="button" onClick={onClose}>Concluir</button></div>
      </Modal>
    );
  }
  return (
    <Modal title="Nova chave de API" onClose={onClose}>
      <div className="field"><label htmlFor="key-name">Nome (qual sistema vai usar)</label>
        <input className="input" id="key-name" autoFocus placeholder="ERP da loja, Integração Qualidoc…" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label htmlFor="key-access">Acesso</label>
        <select id="key-access" className="select" value={access} onChange={(e) => setAccess(e.target.value as typeof access)}>
          <option value="full">Leitura e envio (produtos, estoque, preço e pedidos)</option>
          <option value="read">Só leitura (consulta, não envia)</option>
          <option value="stock">Só estoque (envia o estoque, sem preço, produtos nem pedidos)</option>
        </select></div>
      {create.error && <ErrorBox error={create.error} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
        <button className="btn primary" type="button" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}><KeyRound size={16} /> Criar chave</button>
      </div>
    </Modal>
  );
}
